import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Lock, Users, AlertTriangle } from "lucide-react";

// Reusable renderer: given a published/draft form definition, renders the live form experience.
// Answers are keyed by field id. Handles sections, numbered question headings, conditional show/hide and widths.
const WIDTH = { full: "sm:col-span-2", half: "sm:col-span-1", third: "sm:col-span-1" };
export const LAYOUT_TYPES = ["section", "display", "question", "staff_review", "hidden"];

export function isVisible(f, values) {
  const c = f.conditional;
  if (!c || !c.field_id) return true;
  const cur = values[c.field_id];
  const op = c.operator || "equals";
  if (op === "equals" || op === "not_equals") {
    const eq = String(cur ?? "") === String(c.value ?? "");
    return op === "equals" ? eq : !eq;
  }
  const vals = String(c.value ?? "").split("|").map((s) => s.trim()).filter(Boolean);
  const curs = Array.isArray(cur) ? cur.map(String) : (cur === undefined || cur === null || cur === "" ? [] : [String(cur)]);
  const hit = vals.some((v) => curs.includes(v));
  return op === "not_in" || op === "not_contains" ? !hit : hit;
}

function toggleChoice(arr, c, f) {
  const excl = f.exclusive_choices || [];
  if (arr.includes(c)) return arr.filter((x) => x !== c);
  if (excl.includes(c)) return [c];
  const next = [...arr.filter((x) => !excl.includes(x)), c];
  return f.max_select && next.length > Number(f.max_select) ? arr : next;
}

function WhereToFind({ f }) {
  if (!f.where_to_find) return null;
  return (
    <details className="mb-1 rounded-md bg-sky-50 border border-sky-100 px-2.5 py-1.5" data-testid={`form-where-${f.id}`}>
      <summary className="text-xs font-medium text-sky-800 cursor-pointer">Where to Find This Information</summary>
      <p className="text-xs text-sky-900 mt-1 whitespace-pre-line">{f.where_to_find}</p>
    </details>
  );
}

function SumCheck({ f, fields, values }) {
  const sc = f.sum_check;
  if (!sc) return null;
  const byCode = Object.fromEntries(fields.map((x) => [x.internal_name, values[x.id]]));
  const parts = (sc.fields || []).map((c) => byCode[c]);
  const target = byCode[sc.equals];
  if (parts.some((p) => p === undefined || p === null || p === "") || target === undefined || target === null || target === "") return null;
  const total = parts.reduce((s, p) => s + Number(p), 0);
  if (total === Number(target)) return null;
  return <p className="text-xs text-amber-700 flex items-center gap-1 mb-1" data-testid={`form-sumcheck-${f.id}`}><AlertTriangle className="w-3 h-3" />Your counts add up to {total}, but you entered {target} earlier. Please correct or explain.</p>;
}

export function FormRenderer({ fields = [], values = {}, onChange, disabled = false, dense = false }) {
  const set = (id, v) => onChange && onChange({ ...values, [id]: v });

  const renderInput = (f) => {
    const v = values[f.id] ?? "";
    const off = disabled || !!f.read_only;
    const common = { disabled: off, "data-testid": `form-field-${f.id}` };
    switch (f.field_type) {
      case "long_text":
      case "address":
        return <Textarea value={v} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} rows={3} {...common} />;
      case "number":
      case "currency":
        return <Input type="number" inputMode="decimal" value={v} onChange={(e) => set(f.id, e.target.value === "" ? null : Number(e.target.value))} placeholder={f.placeholder || (f.field_type === "currency" ? "$" : "Leave blank if unknown")} {...common} />;
      case "date":
        return <Input type="date" value={v} onChange={(e) => set(f.id, e.target.value)} {...common} />;
      case "month":
        return <Input type="month" value={v} onChange={(e) => set(f.id, e.target.value)} {...common} />;
      case "datetime":
        return <Input type="datetime-local" value={v} onChange={(e) => set(f.id, e.target.value)} {...common} />;
      case "email":
        return <Input type="email" value={v} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} {...common} />;
      case "dropdown":
        return (
          <Select value={v} onValueChange={(val) => set(f.id, val)} disabled={off}>
            <SelectTrigger data-testid={`form-field-${f.id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent className="bg-white">
              {(f.choices || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case "radio":
      case "yes_no": {
        const opts = f.field_type === "yes_no" ? ["Yes", "No"] : (f.choices || []);
        return (
          <RadioGroup value={v} onValueChange={(val) => set(f.id, val)} disabled={off} className="flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-2 pt-1">
            {opts.map((c) => (
              <label key={c} className="flex items-center gap-1.5 text-sm cursor-pointer">
                <RadioGroupItem value={c} data-testid={`form-field-${f.id}-${c}`} />{c}
              </label>
            ))}
          </RadioGroup>
        );
      }
      case "checkbox":
      case "multi_select": {
        const arr = Array.isArray(v) ? v : [];
        return (
          <div className="flex flex-col gap-1.5 pt-1">
            {f.max_select && <p className="text-[11px] text-slate-400">{arr.length} of {f.max_select} selected</p>}
            {(f.choices || []).map((c) => (
              <label key={c} className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox className="mt-0.5" checked={arr.includes(c)} onCheckedChange={() => set(f.id, toggleChoice(arr, c, f))} disabled={off} data-testid={`form-field-${f.id}-${c}`} />{c}
              </label>
            ))}
          </div>
        );
      }
      case "rating": {
        const lo = Number(f.scale_min ?? 1), hi = Number(f.scale_max ?? 5);
        const nums = Array.from({ length: Math.max(0, hi - lo + 1) }, (_, i) => lo + i);
        return (
          <div className="pt-1">
            <div className="flex flex-wrap gap-1.5">
              {nums.map((n) => (
                <button key={n} type="button" disabled={off} onClick={() => set(f.id, String(n) === String(v) ? null : n)} data-testid={`form-field-${f.id}-${n}`}
                  className={`w-9 h-9 rounded-md border text-sm font-medium transition-colors ${String(v) === String(n) ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-brand"}`}>{n}</button>
              ))}
            </div>
            {(f.scale_low_label || f.scale_high_label) && <div className="flex justify-between text-[11px] text-slate-400 mt-1 max-w-[230px]"><span>{f.scale_low_label}</span><span>{f.scale_high_label}</span></div>}
          </div>
        );
      }
      case "file_upload":
        return <Input type="file" disabled className="cursor-not-allowed" />;
      case "url":
        return <Input type="url" value={v} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder || "https://"} {...common} />;
      default:
        return <Input value={v} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} {...common} />;
    }
  };

  const qn = (f) => (f.question_number ? <span className="text-brand mr-1">Q{f.question_number}.</span> : null);

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${dense ? "gap-3" : "gap-4"}`}>
      {fields.filter((f) => f.field_type !== "hidden").map((f) => {
        if (!isVisible(f, values)) return null;
        if (f.field_type === "section")
          return (
            <div key={f.id} className="sm:col-span-2 mt-2 first:mt-0">
              <h3 className="font-display font-semibold text-slate-900 border-b border-slate-200 pb-1.5">{f.label}</h3>
              {f.help && <p className="text-xs text-slate-500 mt-1">{f.help}</p>}
            </div>
          );
        if (f.field_type === "display" && f.label.length < 70 && !f.label.includes("\n"))
          return <h4 key={f.id} className="sm:col-span-2 mt-3 font-display text-base font-semibold text-slate-800 border-l-4 border-brand pl-2" data-testid={`form-subheading-${f.id}`}>{f.label}</h4>;
        if (f.field_type === "display")
          return <p key={f.id} className="sm:col-span-2 text-sm text-slate-600 bg-slate-50 rounded-md p-3 whitespace-pre-line">{f.label}</p>;
        if (f.field_type === "question")
          return (
            <div key={f.id} className="sm:col-span-2 mt-3 first:mt-0" data-testid={`form-question-${f.question_number || f.id}`}>
              <p className="font-semibold text-slate-900">{qn(f)}{f.label}</p>
              {f.help && <p className="text-xs text-slate-500 mt-0.5 mb-1">{f.help}</p>}
              <WhereToFind f={f} />
            </div>
          );
        if (f.field_type === "staff_review")
          return (
            <div key={f.id} className="sm:col-span-2 rounded-md border border-dashed border-slate-300 p-3 text-sm text-slate-500 flex items-center gap-2" data-testid={`form-staff-placeholder-${f.id}`}>
              <Users className="w-4 h-4" />{qn(f)}{f.label} — shows the agency's current staff and producers when the survey is opened by an agent.
            </div>
          );
        return (
          <div key={f.id} className={WIDTH[f.width] || WIDTH.full} data-testid={f.question_number ? `form-question-${f.question_number}` : undefined}>
            <Label className={`flex items-center gap-1.5 ${f.question_number ? "font-semibold text-slate-900" : ""}`}>
              <span>{qn(f)}{f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}</span>
              {(f.protected || f.read_only) && <Lock className="w-3 h-3 text-slate-400" title={f.read_only ? "Read-only — from your records" : "Protected by Top-Recruit"} />}
            </Label>
            {f.help && <p className="text-xs text-slate-400 mb-1">{f.help}</p>}
            <WhereToFind f={f} />
            <SumCheck f={f} fields={fields} values={values} />
            <div className="mt-1">{renderInput(f)}</div>
          </div>
        );
      })}
    </div>
  );
}
